import React from 'react';
import { Box, Typography } from '@mui/material';
import { CheckCircle, Cancel, RadioButtonUnchecked } from '@mui/icons-material';

// Mirrors the Cognito default password policy
export const passwordCriteria = [
  { label: "At least 8 characters", test: (p) => p.length >= 8 },
  { label: "An uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { label: "A lowercase letter", test: (p) => /[a-z]/.test(p) },
  { label: "A number", test: (p) => /\d/.test(p) },
  { label: "A symbol", test: (p) => /[^A-Za-z0-9\s]/.test(p) },
];

const levels = [
  { label: "Weak", color: "#F87171" },
  { label: "Fair", color: "#FBBF24" },
  { label: "Good", color: "#4DA3FF" },
  { label: "Strong", color: "#34D399" },
];

const mutedText = "rgba(255, 244, 232, 0.55)";

// 0-2 criteria met is Weak, then one level per extra criterion
const getLevel = (metCount) => Math.max(0, metCount - 2);

const countMet = (password) => passwordCriteria.filter(c => c.test(password)).length;

// Anything from Fair up is accepted
export const isStrongEnough = (password) => getLevel(countMet(password)) >= 1;

export const PasswordStrength = ({ password }) => {
  const results = passwordCriteria.map(c => ({ label: c.label, met: c.test(password) }));
  const metCount = results.filter(r => r.met).length;
  const level = getLevel(metCount);
  const { label, color } = levels[level];

  return (
    <Box sx={{ mt: 1.25 }}>
      {/* Strength bar */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box sx={{ display: "flex", flex: 1, gap: 0.75 }}>
          {levels.map((_, i) => (
            <Box key={i} sx={{
              flex: 1,
              height: 4,
              borderRadius: 2,
              backgroundColor: password && i <= level ? color : "rgba(255, 244, 232, 0.12)",
              transition: "background-color 150ms ease",
            }} />
          ))}
        </Box>
        <Typography sx={{ fontSize: 12, fontWeight: 600, minWidth: 44, textAlign: "right", color: password ? color : mutedText }}>
          {password ? label : ""}
        </Typography>
      </Box>

      {/* Criteria */}
      <Box component="ul" sx={{
        listStyle: "none",
        p: 0,
        m: 0,
        mt: 1.25,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        rowGap: 0.5,
        columnGap: 2,
      }}>
        {results.map(({ label, met }) => (
          <Box component="li" key={label} sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            color: met ? "#34D399" : mutedText,
            transition: "color 150ms ease",
          }}>
            {met
              ? <CheckCircle sx={{ fontSize: 14 }} />
              : <RadioButtonUnchecked sx={{ fontSize: 14 }} />
            }
            <Typography sx={{ fontSize: 12.5 }}>{label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export const PasswordMatch = ({ password, repeatPassword }) => {
  if (!repeatPassword) return null;
  const matches = password === repeatPassword;

  return (
    <Box
      aria-live="polite"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        mt: 1,
        color: matches ? "#34D399" : "#F87171",
      }}
    >
      {matches
        ? <CheckCircle sx={{ fontSize: 14 }} />
        : <Cancel sx={{ fontSize: 14 }} />
      }
      <Typography sx={{ fontSize: 12.5 }}>
        {matches ? "Passwords match" : "Passwords don't match"}
      </Typography>
    </Box>
  );
};
